import { CenteredDialog } from '../../components/ui/centered-dialog';
// apps/web/src/pages/pcp/pendencias-central-page.tsx — Fase 10, Plano 02 (D-22..D-24, OMIE-11).
import React, { useEffect, useState } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  Clock,
  Filter,
  RefreshCw,
  ShieldAlert,
  UserPlus,
} from 'lucide-react';
import type {
  PendenciaOperacionalRow,
} from '@pieltec/contracts';
import { apiFetch, apiMutate } from '../../lib/csrf-fetch';
import { obterSessaoAtual, type ObterSessaoResult } from '../../lib/session';
import { DestaqueCancelamentoNfe, podeAgirNaPendencia } from './pendencia-cancelamento-nfe';
import { DestaqueSemRoteiro } from './pendencia-sem-roteiro';
import { TablePagination } from '../../components/ui/table-pagination';
import { useClientPagination } from '../../hooks/use-client-pagination';
import { CampoSelect } from '../../components/ui/campo-select';

function mensagemDaResposta(valor: unknown, padrao: string): string {
  if (typeof valor !== 'object' || valor === null) return padrao;
  const mensagem = (valor as Record<string, unknown>).message;
  return typeof mensagem === 'string' ? mensagem : padrao;
}

export function PendenciasCentralPage() {
  const [pendencias, setPendencias] = useState<PendenciaOperacionalRow[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [filtroTipo, setFiltroTipo] = useState<string>('TODOS');
  const [filtroEstado, setFiltroEstado] = useState<string>('TODOS');
  const [filtroPrioridade, setFiltroPrioridade] = useState<string>('TODOS');
  const [mensagemFeedback, setMensagemFeedback] = useState<{ tipo: 'sucesso' | 'erro'; texto: string } | null>(null);
  // As ações (assumir, atribuir, resolver) exigem remessas:gerenciar na API; quem só consulta (Faturamento, Gestão) vê a fila sem os botões.
  const [sessao, setSessao] = useState<ObterSessaoResult | null>(null);
  const podeAgir = podeAgirNaPendencia(sessao?.autenticado ? sessao.usuario.permissions : null);
  useEffect(() => {
    void obterSessaoAtual().then(setSessao);
  }, []);

  // Modais de Ação
  const [modalAtribuir, setModalAtribuir] = useState<{
    aberto: boolean;
    pendenciaId: string;
    paraUserId: string;
    justificativa: string;
    enviando: boolean;
    isReatribuir: boolean;
  }>({
    aberto: false,
    pendenciaId: '',
    paraUserId: '',
    justificativa: '',
    enviando: false,
    isReatribuir: false,
  });

  const [modalResolver, setModalResolver] = useState<{
    aberto: boolean;
    pendenciaId: string;
    justificativa: string;
    enviando: boolean;
  }>({
    aberto: false,
    pendenciaId: '',
    justificativa: '',
    enviando: false,
  });

  const carregarPendencias = async () => {
    setCarregando(true);
    try {
      const data = await apiFetch<PendenciaOperacionalRow[]>('/api/v1/pendencias');
      setPendencias(data || []);
    } catch (err) {
      setMensagemFeedback({
        tipo: 'erro',
        texto: err instanceof Error ? err.message : 'Falha ao carregar central de pendências.',
      });
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    void carregarPendencias();
  }, []);

  const pendenciasFiltradas = pendencias.filter((p) => {
    if (filtroTipo !== 'TODOS' && p.tipo !== filtroTipo) return false;
    if (filtroEstado !== 'TODOS' && p.estado !== filtroEstado) return false;
    if (filtroPrioridade !== 'TODOS' && p.prioridade !== filtroPrioridade) return false;
    return true;
  });

  const pagination = useClientPagination(pendenciasFiltradas, [filtroTipo, filtroEstado, filtroPrioridade]);

  const handleAssumir = async (id: string) => {
    try {
      const res = await apiMutate(`/api/v1/pendencias/${id}/assumir`, {
        method: 'POST',
      });
      if (!res.ok) {
        const errData: unknown = await res.json().catch(() => null);
        throw new Error(mensagemDaResposta(errData, 'Falha ao assumir pendência.'));
      }
      setMensagemFeedback({ tipo: 'sucesso', texto: 'Pendência assumida com sucesso.' });
      await carregarPendencias();
    } catch (err) {
      setMensagemFeedback({
        tipo: 'erro',
        texto: err instanceof Error ? err.message : 'Erro ao assumir pendência.',
      });
    }
  };

  const handleAtribuir = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!modalAtribuir.justificativa || modalAtribuir.justificativa.trim().length < 5) {
      setMensagemFeedback({ tipo: 'erro', texto: 'A justificativa deve ter no mínimo 5 caracteres.' });
      return;
    }

    setModalAtribuir((prev) => ({ ...prev, enviando: true }));
    const endpoint = modalAtribuir.isReatribuir
      ? `/api/v1/pendencias/${modalAtribuir.pendenciaId}/reatribuir`
      : `/api/v1/pendencias/${modalAtribuir.pendenciaId}/atribuir`;

    try {
      const res = await apiMutate(endpoint, {
        method: 'POST',
        body: {
          paraUserId: modalAtribuir.paraUserId,
          justificativa: modalAtribuir.justificativa,
        },
      });

      if (!res.ok) {
        const errData: unknown = await res.json().catch(() => null);
        throw new Error(mensagemDaResposta(errData, 'Falha ao atribuir pendência.'));
      }

      setMensagemFeedback({
        tipo: 'sucesso',
        texto: `Pendência ${modalAtribuir.isReatribuir ? 'reatribuída' : 'atribuída'} com sucesso.`,
      });
      setModalAtribuir({ aberto: false, pendenciaId: '', paraUserId: '', justificativa: '', enviando: false, isReatribuir: false });
      await carregarPendencias();
    } catch (err) {
      setMensagemFeedback({
        tipo: 'erro',
        texto: err instanceof Error ? err.message : 'Erro ao atribuir pendência.',
      });
      setModalAtribuir((prev) => ({ ...prev, enviando: false }));
    }
  };

  const handleResolver = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!modalResolver.justificativa || modalResolver.justificativa.trim().length < 5) {
      setMensagemFeedback({ tipo: 'erro', texto: 'A justificativa de resolução deve ter no mínimo 5 caracteres.' });
      return;
    }

    setModalResolver((prev) => ({ ...prev, enviando: true }));
    try {
      const res = await apiMutate(`/api/v1/pendencias/${modalResolver.pendenciaId}/resolver`, {
        method: 'POST',
        body: {
          justificativa: modalResolver.justificativa,
        },
      });

      if (!res.ok) {
        const errData: unknown = await res.json().catch(() => null);
        throw new Error(mensagemDaResposta(errData, 'Falha ao resolver pendência.'));
      }

      setMensagemFeedback({ tipo: 'sucesso', texto: 'Pendência resolvida com sucesso.' });
      setModalResolver({ aberto: false, pendenciaId: '', justificativa: '', enviando: false });
      await carregarPendencias();
    } catch (err) {
      setMensagemFeedback({
        tipo: 'erro',
        texto: err instanceof Error ? err.message : 'Erro ao resolver pendência.',
      });
      setModalResolver((prev) => ({ ...prev, enviando: false }));
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto space-y-6">
      {/* Cabeçalho */}
      <header className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-6 rounded-lg border border-slate-200 shadow-sm">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ShieldAlert className="w-7 h-7 text-blue-600" />
            Central de Pendências Operacionais e Fiscais
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Fila unificada de exceções (D-22..D-24). Bloqueio restrito à transição afetada e governança com auditoria.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void carregarPendencias()}
          disabled={carregando}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-100 text-slate-700 rounded-md text-sm font-medium hover:bg-slate-200 focus:ring-2 focus:ring-blue-500 disabled:opacity-50 touch-manipulation"
        >
          <RefreshCw className={`w-4 h-4 ${carregando ? 'animate-spin' : ''}`} />
          Atualizar Fila
        </button>
      </header>

      {/* Alerta de Feedback */}
      {mensagemFeedback && (
        <div
          role="alert"
          aria-live="polite"
          className={`p-4 rounded-lg flex items-center justify-between gap-3 text-sm font-medium ${
            mensagemFeedback.tipo === 'sucesso'
              ? 'bg-emerald-50 text-emerald-900 border border-emerald-200'
              : 'bg-rose-50 text-rose-900 border border-rose-200'
          }`}
        >
          <span>{mensagemFeedback.texto}</span>
          <button
            type="button"
            onClick={() => setMensagemFeedback(null)}
            className="text-xs underline font-bold hover:opacity-80 p-1"
          >
            Fechar
          </button>
        </div>
      )}

      {/* Cartões de Indicadores */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <div className="p-4 bg-white rounded-lg border border-slate-200 shadow-sm">
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Total de Pendências</div>
          <div className="text-2xl font-bold text-slate-900 mt-1">{pendencias.length}</div>
        </div>
        <div className="p-4 bg-white rounded-lg border border-slate-200 shadow-sm">
          <div className="text-xs font-semibold text-amber-600 uppercase tracking-wider">Abertas</div>
          <div className="text-2xl font-bold text-amber-700 mt-1">
            {pendencias.filter((p) => p.estado === 'ABERTA').length}
          </div>
        </div>
        <div className="p-4 bg-white rounded-lg border border-slate-200 shadow-sm">
          <div className="text-xs font-semibold text-blue-600 uppercase tracking-wider">Em Análise</div>
          <div className="text-2xl font-bold text-blue-700 mt-1">
            {pendencias.filter((p) => p.estado === 'EM_ANALISE').length}
          </div>
        </div>
        <div className="p-4 bg-white rounded-lg border border-slate-200 shadow-sm">
          <div className="text-xs font-semibold text-rose-600 uppercase tracking-wider">Alta Prioridade</div>
          <div className="text-2xl font-bold text-rose-700 mt-1">
            {pendencias.filter((p) => p.prioridade === 'ALTA' && p.estado !== 'RESOLVIDA').length}
          </div>
        </div>
      </div>

      {/* Tabela de Pendências */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-200 flex flex-col md:flex-row justify-between items-start md:items-center gap-3 bg-slate-50">
          <div>
            <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
              <Clock className="w-5 h-5 text-blue-600" />
              Fila de Exceções e Ações Operacionais
            </h2>
            <p className="text-xs text-slate-500">
              Gerencie exceções fiscais, divergências e itens pendentes de conciliação.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Filter className="w-4 h-4 text-slate-400" />
            <CampoSelect
              aria-label="Filtrar por tipo"
              size="sm"
              value={filtroTipo}
              onValueChange={setFiltroTipo}
              options={[
                { value: 'TODOS', label: 'Todos os Tipos' },
                { value: 'DUPLICIDADE', label: 'Duplicidade' },
                { value: 'ITEM_SEM_CONCILIACAO', label: 'Item s/ Conciliação' },
                { value: 'CONVERSAO_FORA_TOLERANCIA', label: 'Conversão Fora Tol.' },
                { value: 'DIVERGENCIA_FISCAL', label: 'Divergência Fiscal' },
                { value: 'INDISPONIBILIDADE_EXTERNA', label: 'Indisponib. Externa' },
                { value: 'CANCELAMENTO_NFE', label: 'NF cancelada na Omie' },
              ]}
              className="w-52 text-xs text-slate-700"
            />

            <CampoSelect
              aria-label="Filtrar por estado"
              size="sm"
              value={filtroEstado}
              onValueChange={setFiltroEstado}
              options={[
                { value: 'TODOS', label: 'Todos os Estados' },
                { value: 'ABERTA', label: 'Aberta' },
                { value: 'EM_ANALISE', label: 'Em Análise' },
                { value: 'RESOLVIDA', label: 'Resolvida' },
              ]}
              className="w-44 text-xs text-slate-700"
            />

            <CampoSelect
              aria-label="Filtrar por prioridade"
              size="sm"
              value={filtroPrioridade}
              onValueChange={setFiltroPrioridade}
              options={[
                { value: 'TODOS', label: 'Todas as Prioridades' },
                { value: 'ALTA', label: 'Alta' },
                { value: 'MEDIA', label: 'Média' },
                { value: 'BAIXA', label: 'Baixa' },
              ]}
              className="w-48 text-xs text-slate-700"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs text-slate-800">
            <thead className="bg-slate-100 border-b border-slate-200 text-slate-600 uppercase font-semibold text-[11px] tracking-wider">
              <tr>
                <th className="p-3">Tipo</th>
                <th className="p-3">Prioridade</th>
                <th className="p-3">Entidade Vinculada</th>
                <th className="p-3">Estado</th>
                <th className="p-3">Responsável</th>
                <th className="p-3">Data</th>
                <th className="p-3 text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {pendenciasFiltradas.length === 0 ? (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-slate-500">
                    Nenhuma pendência encontrada para os filtros selecionados.
                  </td>
                </tr>
              ) : (
                pagination.pageItems.map((p) => (
                  <tr key={p.id} className="hover:bg-slate-50">
                    <td className="p-3 font-semibold text-slate-900">
                      <span className="px-2 py-0.5 rounded text-[11px] font-mono bg-slate-100 text-slate-800 border border-slate-200">
                        {p.tipo}
                      </span>
                      {/* L-63: a NF de remessa cancelada na Omie leva ao estorno do recebimento (quem tem remessas:estornar estorna). */}
                      <DestaqueCancelamentoNfe pendencia={p} />
                      {/* D-25 (17.3): lote sem roteiro, produto sem cadastro e remessa recusada levam ao cadastro da peça ou do roteiro. */}
                      <DestaqueSemRoteiro pendencia={p} permissoes={sessao?.autenticado ? sessao.usuario.permissions : null} />
                    </td>
                    <td className="p-3">
                      <span
                        className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                          p.prioridade === 'ALTA'
                            ? 'bg-rose-100 text-rose-800 border border-rose-200'
                            : p.prioridade === 'MEDIA'
                            ? 'bg-amber-100 text-amber-800 border border-amber-200'
                            : 'bg-slate-100 text-slate-700'
                        }`}
                      >
                        {p.prioridade}
                      </span>
                    </td>
                    <td className="p-3 font-mono text-slate-600">
                      <strong>{p.entidadeTipo}:</strong> {p.entidadeId.slice(0, 8)}...
                    </td>
                    <td className="p-3">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold ${
                          p.estado === 'ABERTA'
                            ? 'bg-amber-50 text-amber-800 border border-amber-300'
                            : p.estado === 'EM_ANALISE'
                            ? 'bg-blue-50 text-blue-800 border border-blue-300'
                            : 'bg-emerald-50 text-emerald-800 border border-emerald-300'
                        }`}
                      >
                        {p.estado === 'ABERTA' && <AlertCircle className="w-3 h-3 text-amber-600" />}
                        {p.estado === 'EM_ANALISE' && <Clock className="w-3 h-3 text-blue-600" />}
                        {p.estado === 'RESOLVIDA' && <CheckCircle2 className="w-3 h-3 text-emerald-600" />}
                        {p.estado}
                      </span>
                    </td>
                    <td className="p-3 text-slate-600">
                      {p.responsavelUserId ? (
                        <span className="font-mono text-xs text-blue-700">{p.responsavelUserId.slice(0, 8)}...</span>
                      ) : (
                        <span className="text-slate-400 italic">Não atribuído</span>
                      )}
                    </td>
                    <td className="p-3 text-slate-500 text-xs">
                      {new Date(p.createdAt).toLocaleDateString('pt-BR')}
                    </td>
                    <td className="p-3 text-right space-x-2">
                      {podeAgir && p.estado !== 'RESOLVIDA' && (
                        <>
                          <button
                            type="button"
                            onClick={() => void handleAssumir(p.id)}
                            className="px-2.5 py-1 bg-slate-100 hover:bg-slate-200 text-slate-800 rounded text-xs font-semibold touch-manipulation"
                          >
                            Assumir
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              setModalAtribuir({
                                aberto: true,
                                pendenciaId: p.id,
                                paraUserId: '',
                                justificativa: '',
                                enviando: false,
                                isReatribuir: p.estado === 'EM_ANALISE',
                              })
                            }
                            className="px-2.5 py-1 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded text-xs font-semibold touch-manipulation"
                          >
                            {p.estado === 'EM_ANALISE' ? 'Reatribuir' : 'Atribuir'}
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              setModalResolver({
                                aberto: true,
                                pendenciaId: p.id,
                                justificativa: '',
                                enviando: false,
                              })
                            }
                            className="px-2.5 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-xs font-semibold touch-manipulation"
                          >
                            Resolver
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          {pendenciasFiltradas.length > 0 && (
            <TablePagination
              page={pagination.page}
              pageSize={pagination.pageSize}
              totalItems={pendenciasFiltradas.length}
              itemLabel="pendências"
              onPageChange={pagination.setPage}
              onPageSizeChange={pagination.setPageSize}
            />
          )}
        </div>
      </div>

      {/* Modal de Atribuição / Reatribuição */}
      {modalAtribuir.aberto && (
        <CenteredDialog title="Detalhes da operação" onClose={() => setModalAtribuir((prev) => ({ ...prev, aberto: false }))}>
          <div className="bg-white border border-slate-200 rounded-lg shadow-xl max-w-md w-full p-6 space-y-4">
            <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-blue-600" />
              {modalAtribuir.isReatribuir ? 'Reatribuir Pendência' : 'Atribuir Responsável'}
            </h3>
            <p className="text-xs text-slate-500">
              A alteração de responsável é auditada no sistema (D-23) e exige justificativa.
            </p>

            <form onSubmit={(evento) => void handleAtribuir(evento)} className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1" htmlFor="field-pendencias-central-page-19227">ID do Usuário Responsável (UUID):</label>
                <input id="field-pendencias-central-page-19227"
                  type="text"
                  required
                  value={modalAtribuir.paraUserId}
                  onChange={(e) => setModalAtribuir((prev) => ({ ...prev, paraUserId: e.target.value }))}
                  placeholder="00000000-0000-4000-8000-000000000001"
                  className="w-full border border-slate-300 rounded p-2 text-xs font-mono"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1" htmlFor="field-pendencias-central-page-19810">
                  Justificativa Operacional (mínimo 5 caracteres):
                </label>
                <textarea id="field-pendencias-central-page-19810"
                  required
                  rows={3}
                  value={modalAtribuir.justificativa}
                  onChange={(e) => setModalAtribuir((prev) => ({ ...prev, justificativa: e.target.value }))}
                  placeholder="Ex: Atribuído para conferência técnica de material."
                  className="w-full border border-slate-300 rounded p-2 text-xs"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => setModalAtribuir((prev) => ({ ...prev, aberto: false }))}
                  className="px-3 py-1.5 border border-slate-300 text-slate-700 rounded text-xs font-medium"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={modalAtribuir.enviando}
                  className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs font-semibold disabled:opacity-50"
                >
                  {modalAtribuir.enviando ? 'Gravando...' : 'Confirmar Atribuição'}
                </button>
              </div>
            </form>
          </div>
        </CenteredDialog>
      )}

      {/* Modal de Resolução */}
      {modalResolver.aberto && (
        <CenteredDialog title="Resolver Pendência Operacional" onClose={() => setModalResolver((prev) => ({ ...prev, aberto: false }))}>
          <div className="bg-white border border-slate-200 rounded-lg shadow-xl max-w-md w-full p-6 space-y-4">
            <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              Resolver Pendência Operacional
            </h3>
            <p className="text-xs text-slate-500">
              Descreva as ações tomadas para a resolução desta exceção (D-23).
            </p>

            <form onSubmit={(evento) => void handleResolver(evento)} className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1" htmlFor="field-pendencias-central-page-22045">
                  Justificativa de Resolução (mínimo 5 caracteres):
                </label>
                <textarea id="field-pendencias-central-page-22045"
                  required
                  rows={3}
                  value={modalResolver.justificativa}
                  onChange={(e) => setModalResolver((prev) => ({ ...prev, justificativa: e.target.value }))}
                  placeholder="Ex: Item vinculado ao cadastro de serviço correspondente e conferência confirmada."
                  className="w-full border border-slate-300 rounded p-2 text-xs"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => setModalResolver((prev) => ({ ...prev, aberto: false }))}
                  className="px-3 py-1.5 border border-slate-300 text-slate-700 rounded text-xs font-medium"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={modalResolver.enviando}
                  className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-xs font-semibold disabled:opacity-50"
                >
                  {modalResolver.enviando ? 'Resolvendo...' : 'Confirmar Resolução'}
                </button>
              </div>
            </form>
          </div>
        </CenteredDialog>
      )}
    </div>
  );
}
